// วาดการ์ดรูปภาพสำหรับแชร์ผลชีวิตลง canvas (docs/PLAN.md เฟส 4)
// วาดในเบราว์เซอร์ทั้งหมด ไม่ใช้เซิร์ฟเวอร์ จึงไม่กินโควตาของ Cloudflare
// ขนาด 1080 × 1350 (4:5) เหมาะกับฟีดของโซเชียลส่วนใหญ่ ใช้สีโทนสว่างเสมอไม่ว่าธีมของเครื่องจะเป็นแบบไหน

import { cellBounds, type LatLon } from "../engine/grid";
import { MAP_HEIGHT, MAP_WIDTH, project } from "../geo/projection";
import type { Realm, SpeciesRange } from "../geo/range";
import { wrapText, type ShareCardData } from "../shared/share-card";
import type { WorldShapes } from "./geo";

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 1350;
const PAD = 60;
const INNER = CARD_WIDTH - PAD * 2;
// Inter ไม่มีตัวอักษรไทย ตัวไทยจึงใช้ Noto Sans Thai (โหลดจาก Google Fonts ใน index.html)
const FONT = '"Inter", "Noto Sans Thai", system-ui, sans-serif';

// ธีมป่าแบบสารคดีธรรมชาติ เหมือนหน้าเกม (public/app.css)
const C = {
  bg: "#0c1510", surface: "#182a1f", fg: "#edf2e8", muted: "#9fb29a", line: "#25392b", accent: "#f2b632", header: "#1a3020",
  sea: "#0e1f2b", land: "#1d2a1e", border: "#34472f", range: "#8fd46e", route: "#f2b632", pin: "#ff6b4a",
};

export interface ShareMap {
  world: WorldShapes;
  range: SpeciesRange | null;
  path: readonly LatLon[];
  realm: Realm; // สัตว์บกไม่ระบายถิ่นที่อยู่ทับทะเล สัตว์ทะเลไม่ระบายทับแผ่นดิน (เหมือน WorldMap)
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

/** กรอบที่ซูมดู: เส้นทางชีวิตและถิ่นที่อยู่ใกล้ๆ สัดส่วน 2:1 ถ้ากว้างเกินครึ่งโลกแสดงทั้งโลก */
function focus(map: ShareMap): { x: number; y: number; w: number; h: number } {
  const pts = map.path.map((p) => project(p.lon, p.lat));
  for (const { cell } of map.range?.cells ?? []) {
    const b = cellBounds(cell);
    if (map.path.some((p) => Math.abs(b.south + 0.5 - p.lat) <= 12 && Math.abs(((b.west + 0.5 - p.lon + 540) % 360) - 180) <= 12)) {
      pts.push(project(b.west, b.north), project(b.east, b.south));
    }
  }
  const world = { x: 0, y: 0, w: MAP_WIDTH, h: MAP_HEIGHT };
  if (!pts.length) return world;
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  if (x1 - x0 > MAP_WIDTH * 0.55) return world;
  let w = Math.max((x1 - x0) * 1.4, 90);
  let h = Math.max((y1 - y0) * 1.4, 45);
  if (w / h < 2) w = h * 2;
  else h = w / 2;
  if (w >= MAP_WIDTH) return world;
  const cx = Math.min(Math.max((x0 + x1) / 2, w / 2), MAP_WIDTH - w / 2);
  const cy = Math.min(Math.max((y0 + y1) / 2, h / 2), MAP_HEIGHT - h / 2);
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}

function drawMap(ctx: CanvasRenderingContext2D, map: ShareMap, x: number, y: number, w: number, h: number) {
  ctx.save();
  roundRect(ctx, x, y, w, h, 28);
  ctx.fillStyle = C.sea;
  ctx.fill();
  ctx.clip();
  const box = focus(map);
  const scale = Math.min(w / box.w, h / box.h);
  ctx.translate(x + (w - box.w * scale) / 2, y + (h - box.h * scale) / 2);
  ctx.scale(scale, scale);
  ctx.translate(-box.x, -box.y);
  const px = 1 / scale; // หนึ่งพิกเซลบนการ์ด ในหน่วยของแผนที่

  const land = new Path2D(map.world.countries.map((c) => c.d).join(""));
  ctx.fillStyle = C.land;
  ctx.fill(land);
  ctx.strokeStyle = C.border;
  ctx.lineWidth = 1.5 * px;
  ctx.stroke(land);

  // รวมช่องระดับเดียวกันเป็นเส้นเดียวแล้วระบายครั้งเดียว ขยายขอบเล็กน้อยไม่ให้เห็นรอยต่อระหว่างช่อง
  const levels = [new Path2D(), new Path2D(), new Path2D(), new Path2D()];
  for (const { cell, level } of map.range?.cells ?? []) {
    const b = cellBounds(cell);
    const [ax, ay] = project(b.west, b.north);
    const [bx, by] = project(b.east, b.south);
    levels[Math.min(3, Math.max(1, level))].rect(ax - 0.4 * px, ay - 0.4 * px, bx - ax + 0.8 * px, by - ay + 0.8 * px);
  }
  ctx.save();
  if (map.realm === "land") ctx.clip(land);
  if (map.realm === "sea") ctx.clip(new Path2D(map.world.sphere + map.world.countries.map((c) => c.d).join("")), "evenodd");
  ctx.fillStyle = C.range;
  [0.3, 0.45, 0.65].forEach((alpha, i) => {
    ctx.globalAlpha = alpha;
    ctx.fill(levels[i + 1], "nonzero");
  });
  ctx.restore();

  const pts = map.path.map((p) => project(p.lon, p.lat));
  if (pts.length > 1) {
    ctx.beginPath();
    pts.forEach(([px0, py0], i) => (i && Math.abs(px0 - pts[i - 1][0]) < MAP_WIDTH / 2 ? ctx.lineTo(px0, py0) : ctx.moveTo(px0, py0)));
    ctx.strokeStyle = C.route;
    ctx.lineWidth = 5 * px;
    ctx.setLineDash([12 * px, 9 * px]);
    ctx.lineCap = "round";
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(pts[0][0], pts[0][1], 9 * px, 0, Math.PI * 2);
    ctx.fillStyle = C.surface;
    ctx.fill();
    ctx.lineWidth = 5 * px;
    ctx.stroke();
  }
  const here = pts[pts.length - 1];
  if (here) {
    ctx.beginPath();
    ctx.arc(here[0], here[1], 13 * px, 0, Math.PI * 2);
    ctx.fillStyle = C.pin;
    ctx.fill();
    ctx.lineWidth = 4 * px;
    ctx.strokeStyle = C.surface;
    ctx.stroke();
  }
  ctx.restore();
}

function text(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, size: number, color: string, weight = 400, style = "") {
  ctx.font = `${style} ${weight} ${size}px ${FONT}`.trim();
  ctx.fillStyle = color;
  ctx.fillText(s, x, y);
}

/** วาดการ์ดลง canvas ที่ให้มา (ขนาด 1080 × 1350) */
export function drawShareCard(canvas: HTMLCanvasElement, data: ShareCardData, map: ShareMap | null, site: string): void {
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const ctx = canvas.getContext("2d")!;
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  // แถบหัว
  ctx.fillStyle = C.header;
  ctx.fillRect(0, 0, CARD_WIDTH, 96);
  ctx.fillStyle = C.accent;
  ctx.fillRect(0, 92, CARD_WIDTH, 4);
  text(ctx, "WILD-LIFEPACK", PAD, 62, 32, "#ffffff", 800);
  ctx.textAlign = "right";
  text(ctx, "บันทึกภาคสนาม · จบหนึ่งชีวิต", CARD_WIDTH - PAD, 60, 28, C.accent, 600);
  ctx.textAlign = "left";

  // ชื่อสัตว์
  let y = 200;
  ctx.font = `700 84px ${FONT}`;
  for (const line of wrapText(data.name, INNER, (s) => ctx.measureText(s).width, 2)) {
    text(ctx, line, PAD, y, 84, C.fg, 700);
    y += 96;
  }
  text(ctx, data.sci, PAD, y - 30, 40, C.muted, 400, "italic");
  y += 20;
  if (data.generation) {
    const label = `รุ่นที่ ${data.generation.toLocaleString("th-TH")} ของสายตระกูล`;
    ctx.font = `600 30px ${FONT}`;
    const w = ctx.measureText(label).width + 40;
    roundRect(ctx, PAD, y - 6, w, 48, 24);
    ctx.fillStyle = "rgba(242, 182, 50, .14)";
    ctx.fill();
    text(ctx, label, PAD + 20, y + 28, 30, C.accent, 600);
    y += 62;
  }

  // ส่วนล่าง (สถิติ สถานที่ ความสำเร็จ) วัดความสูงก่อน แผนที่ใช้ที่ที่เหลือ
  const gap = 24;
  const cw = (INNER - gap) / 2;
  const ch = 150;
  ctx.font = `400 30px ${FONT}`;
  const placeLines = wrapText(data.places ?? "", INNER, (s) => ctx.measureText(s).width, 2);
  ctx.font = `600 28px ${FONT}`;
  const achievementLine = data.achievements.length ? wrapText(`ความสำเร็จใหม่: ${data.achievements.join(" · ")}`, INNER, (s) => ctx.measureText(s).width, 1)[0] : null;
  const bottom = ch * 2 + gap + 54 + placeLines.length * 42 + (achievementLine ? 42 : 0);
  const footerTop = CARD_HEIGHT - 130;

  // แผนที่
  if (map) {
    const h = Math.min(440, footerTop - y - 30 - bottom);
    if (h >= 200) {
      drawMap(ctx, map, PAD, y, INNER, h);
      y += h + 30;
    }
  }

  // สถิติ 2 × 2
  data.stats.forEach((st, i) => {
    const x = PAD + (i % 2) * (cw + gap);
    const top = y + Math.floor(i / 2) * (ch + gap);
    roundRect(ctx, x, top, cw, ch, 24);
    ctx.fillStyle = C.surface;
    ctx.fill();
    ctx.strokeStyle = C.line;
    ctx.lineWidth = 2;
    ctx.stroke();
    text(ctx, st.label, x + 28, top + 46, 28, C.muted);
    ctx.font = `700 46px ${FONT}`;
    const value = wrapText(st.value, cw - 56, (s) => ctx.measureText(s).width, 1)[0];
    text(ctx, value, x + 28, top + (st.sub ? 100 : 112), 46, C.fg, 700);
    if (st.sub) {
      ctx.font = `400 26px ${FONT}`;
      text(ctx, wrapText(st.sub, cw - 56, (s) => ctx.measureText(s).width, 1)[0], x + 28, top + 136, 26, C.muted);
    }
  });
  y += ch * 2 + gap + 54;

  // สถานที่และความสำเร็จ
  for (const line of placeLines) {
    text(ctx, line, PAD, y, 30, C.fg);
    y += 42;
  }
  if (achievementLine) text(ctx, achievementLine, PAD, y + 4, 28, C.accent, 600);

  // ท้ายการ์ด
  ctx.fillStyle = C.line;
  ctx.fillRect(PAD, CARD_HEIGHT - 110, INNER, 2);
  text(ctx, "คุณจะเกิดเป็นสัตว์อะไร?", PAD, CARD_HEIGHT - 52, 34, C.fg, 700);
  ctx.textAlign = "right";
  text(ctx, site, CARD_WIDTH - PAD, CARD_HEIGHT - 52, 30, C.accent, 600);
  ctx.textAlign = "left";
}

/** วาดแล้วแปลงเป็นไฟล์ PNG (รอฟอนต์โหลดเสร็จก่อน ไม่งั้นตัวอักษรไทยอาจเพี้ยน) */
export async function renderShareCard(data: ShareCardData, map: ShareMap | null, site: string): Promise<Blob> {
  await document.fonts?.ready;
  const canvas = document.createElement("canvas");
  drawShareCard(canvas, data, map, site);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("สร้างรูปไม่สำเร็จ"))), "image/png"));
}
